/**
 * ModalPortal - World-class modal system using React Portals
 * Features:
 * - Renders content outside of parent DOM hierarchy
 * - Proper z-index layering using CSS custom properties
 * - Accessibility features (focus trap, escape key)
 * - Backdrop click to close
 * - Mobile responsive
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal should close
 * @param {React.ReactNode} props.children - Modal content
 * @param {'small'|'medium'|'large'|'xl'|'full'} props.size - Modal size
 * @param {boolean} props.showBackdrop - Whether to show backdrop
 * @param {boolean} props.closeOnBackdrop - Whether to close on backdrop click
 * @param {boolean} props.closeOnEscape - Whether to close on escape key
 * @param {string} props.className - Additional CSS classes
 */

import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

function ModalPortal({ 
  isOpen, 
  onClose, 
  children, 
  size = 'medium',
  showBackdrop = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = '',
}) {
  const modalRef = useRef(null)
  const previousActiveElement = useRef(null)

  // Size styles configuration
  const sizeStyles = {
    small: { maxWidth: '400px', width: '90%' },
    medium: { maxWidth: '600px', width: '90%' },
    large: { maxWidth: '800px', width: '90%' },
    xl: { maxWidth: '1200px', width: '95%' },
    full: { width: '100%', height: '100%', margin: '0', borderRadius: '0' },
  }

  // Memoized backdrop click handler to prevent re-renders
  const handleBackdropClick = useCallback((e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose()
    }
  }, [closeOnBackdrop, onClose])

  // Focus management
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement
      modalRef.current?.focus()
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus()
    }
  }, [isOpen])

  // Escape key handler
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, closeOnEscape, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = 'unset'
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  const modal = (
    <div
      className={`modal-portal-overlay ${className}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: '99999',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: showBackdrop ? 'var(--modal-backdrop)' : 'transparent',
        backdropFilter: showBackdrop ? 'blur(4px)' : 'none',
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="modal-portal-content"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={{
          backgroundColor: 'var(--modal-bg)',
          borderRadius: '12px',
          boxShadow: 'var(--modal-shadow)',
          border: '1px solid var(--modal-border)',
          maxHeight: 'calc(100vh - 40px)',
          overflow: 'auto',
          outline: 'none',
          ...sizeStyles[size],
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )

  return createPortal(modal, document.getElementById('modal-root') || document.body)
}

export default ModalPortal
